import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const ThemeContext = createContext(null);
const STORAGE_KEY = "miledeep-theme";

export const THEMES = [
  { key: "midnight", label: "Midnight Navy", hint: "Signature deep navy · gold accents" },
  { key: "light", label: "Light", hint: "Bright cream · gold accents" },
  { key: "classic", label: "Classic Dark", hint: "Original black · gold accents" },
  { key: "erp", label: "Miledeep ERP", hint: "Clean ERP workspace · blue & purple" },
];
export const THEME_KEYS = THEMES.map((t) => t.key);
const DEFAULT_THEME = "midnight";

function normalize(t) {
  if (t === "dark") return "classic"; // backward compat with v1.0/1.1 stored value
  return THEME_KEYS.includes(t) ? t : DEFAULT_THEME;
}

function applyTheme(theme) {
  const root = document.documentElement;
  const body = document.body;
  THEME_KEYS.forEach((k) => {
    body.classList.toggle(k, k === theme);
    root.classList.toggle(k, k === theme);
  });
  root.style.colorScheme = ["light", "erp"].includes(theme) ? "light" : "dark";
}

export function ThemeProvider({ children, user }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_THEME;
    return normalize(localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME);
  });

  // hydrate from user preference once logged in
  useEffect(() => {
    if (user?.theme) {
      const normalized = normalize(user.theme);
      if (normalized !== theme) setThemeState(normalized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.theme]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback(async (next) => {
    const t = normalize(next);
    setThemeState(t);
    if (user) {
      try { await api.patch("/profile", { theme: t }); } catch (_e) { /* ignore */ }
    }
  }, [user]);

  const cycle = useCallback(() => {
    const idx = THEME_KEYS.indexOf(theme);
    setTheme(THEME_KEYS[(idx + 1) % THEME_KEYS.length]);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
